import { ModalConfirmCreateHierarchy } from 'components/modal/ModalConfirmCreateHierarchy';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useCreatePortfolio } from 'services/queryClient/wrapperHooks/portfolio/useCreatePortfolio';
import { useEditPortfolio } from 'services/queryClient/wrapperHooks/portfolio/useEditPortfolio';
import { useSalesChannelList } from 'services/queryClient/wrapperHooks/salesChannel/useSalesChannelList';
import { useGetFilterCommercialHierarchy } from 'services/queryClient/wrapperHooks/useGetFilterCommercialHierarchy';
import { useSalesForceList } from 'services/queryClient/wrapperHooks/useSalesForceList';
import {
	renderClient,
	renderFamily,
	renderProduct,
} from 'utils/forms/autocomplete';
import { currencyToNumber } from 'utils/functions/currencyToNumber';

import { useProductFamilyLogic } from '../../hooks/useProductFamilyLogic';
import { useSalesZoneList } from '../../services/queryClient/wrapperHooks/useSalesZoneList';
import { makeGenericFormInputStructure } from '../../utils/forms/defaultInputs';
import {
	columnFlexProps,
	defaultFormStyle,
	defaultInputStyle,
	formFlexProps,
	rowFlexProps,
} from '../../utils/forms/defaultsProps';
import {
	validateByRegex,
	validateMin,
	validateMinDecimal,
	validateRequired,
} from '../../utils/forms/validations';
import { filterNotInformedOptions } from '../../utils/functions/filterNotInformedOptions';
import { LoadingSpinner } from '../loading/Loading';
import GenericForm, { IFormRows } from './GenericForm';

interface IFormCadastrarEditarCarteira {
	initialState: any;
	portfolioId?: number;
	autocompleteAdditionalData?: any;
}

const FormCadastrarEditarCarteira = ({
	initialState,
	portfolioId = 0,
	autocompleteAdditionalData,
}: IFormCadastrarEditarCarteira) => {
	const history = useHistory();

	const isEditing = !!portfolioId;

	const { data: SalesChannelData, isLoading: isLoadingSalesChannelData } =
		useSalesChannelList();
	const { data: SalesZoneData, isLoading: IsLoadingSalesZone } =
		useSalesZoneList();
	const {
		data: CommercialHierarchyData,
		isLoading: isLoadingCommercialHierarchy,
	} = useGetFilterCommercialHierarchy(undefined);
	const { data: SalesForceData, isLoading: isLoadingSalesForce } =
		useSalesForceList();

	const [formInitialState, setFormInitialState] = useState<any>();
	const [forceFamilyValue, setForceFamilyValue] = useState<any>(null);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [pendingPayload, setPendingPayload] = useState<any>(null);

	const {
		familyId,
		handleOnChangeFamily,
		handleOnChangeProduct,
		isDisabledProductAutocomplete,
		shouldClearAutocompleteProduct,
	} = useProductFamilyLogic({
		initialFamilyId: initialState?.familia_id,
		initialProduct: autocompleteAdditionalData?.product,
	});

	const handleOnChangeFamilyWithLog = useCallback(
		(value: any) => {
			handleOnChangeFamily(value);
		},
		[handleOnChangeFamily]
	);

	const handleOnChangeProductWithLog = useCallback(
		(value: any, formProps: any) => {
			handleOnChangeProduct(value);

			if (value?.familia_id && value?.nome_familia) {
				const familyData = {
					id: value.familia_id,
					nome_familia: value.nome_familia,
					empresa_id: value.empresa_id,
				};

				setForceFamilyValue(familyData);
				handleOnChangeFamilyWithLog(familyData);

				if (formProps) {
					formProps.setValue('familia_id', value.familia_id);
				}

				setTimeout(() => {
					setForceFamilyValue(null);
				}, 100);
			}
		},
		[handleOnChangeProduct, handleOnChangeFamilyWithLog]
	);

	const handleOpenModal = useCallback(() => {
		setIsModalOpen(true);
	}, []);

	const handleCloseModal = useCallback(() => {
		setIsModalOpen(false);
		setPendingPayload(null);
	}, []);

	const { mutate: createPortfolio } = useCreatePortfolio(handleOpenModal);
	const { mutate: updatePortfolio } = useEditPortfolio(
		portfolioId,
		handleOpenModal
	);

	const handleConfirmCreateHierarchy = useCallback(() => {
		if (pendingPayload) {
			const bodyWithCreate = {
				...pendingPayload,
				is_create_hierarchy: true,
			};
			if (isEditing) {
				updatePortfolio(bodyWithCreate);
			} else {
				createPortfolio(bodyWithCreate);
			}
		}
		setIsModalOpen(false);
	}, [pendingPayload, isEditing, createPortfolio, updatePortfolio]);

	useEffect(() => {
		setFormInitialState(initialState);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initialState]);

	const goToPortfolio = useCallback(() => {
		history.goBack();
	}, [history]);

	const salesChannelOptions = useMemo(() => {
		const filteredChannels = filterNotInformedOptions(
			SalesChannelData || [],
			'nome_canal_venda'
		);

		return filteredChannels.map(channel => (
			<option
				key={channel?.id}
				value={channel?.id}
			>
				{channel?.nome_canal_venda}
			</option>
		));
	}, [SalesChannelData]);

	const zoneOptions = useMemo(() => {
		const filteredZones = filterNotInformedOptions(
			SalesZoneData?.data || [],
			'nome_zona_venda'
		);

		return filteredZones.map(s => (
			<option
				key={s?.id}
				value={s?.id}
			>
				{s?.nome_zona_venda}
			</option>
		));
	}, [SalesZoneData]);

	const marketOptions = useMemo(() => {
		const filteredMarkets = filterNotInformedOptions(
			CommercialHierarchyData?.mercados || [],
			'nome_mercado'
		);

		return filteredMarkets.map(m => (
			<option
				key={m?.id}
				value={m?.id}
			>
				{m?.nome_mercado}
			</option>
		));
	}, [CommercialHierarchyData]);

	const salesForceOptions = useMemo(() => {
		const filteredSalesForce = filterNotInformedOptions(
			SalesForceData?.data || [],
			'nome_forca_venda'
		);

		return filteredSalesForce.map(sf => (
			<option
				key={sf?.id}
				value={sf?.id}
			>
				{sf?.nome_forca_venda}
			</option>
		));
	}, [SalesForceData]);

	const memberOptions = useMemo(() => {
		const filteredMembers = filterNotInformedOptions(
			CommercialHierarchyData?.membros || [],
			'nome_membro'
		);

		return filteredMembers.map(m => (
			<option
				key={m?.id}
				value={m?.id}
			>
				{m?.nome_membro}
			</option>
		));
	}, [CommercialHierarchyData]);

	const handleOnChangeCode = useCallback((event, form) => {
		form.setValue('sequencia_codigo', '');
	}, []);

	const onSubmit = useCallback(
		values => {
			const body = {
				data: values?.data,
				codigo: values?.codigo,
				sequencia_codigo: values?.sequencia_codigo,
				cliente_id: values?.cliente_id
					? Number(values?.cliente_id)
					: null,
				familia_id: values?.familia_id
					? Number(values?.familia_id)
					: null,
				produto_id: values?.produto_id
					? Number(values?.produto_id)
					: null,
				quantidade: currencyToNumber(values?.quantidade),
				canal_venda_id: values?.canal_venda_id
					? Number(values?.canal_venda_id)
					: null,
				mercado_id: values?.mercado_id
					? Number(values?.mercado_id)
					: null,
				forca_venda_id: values?.forca_venda_id
					? Number(values?.forca_venda_id)
					: null,
				zona_venda_id: values?.zona_venda_id
					? Number(values?.zona_venda_id)
					: null,
				membro_id: values?.membro_id ? Number(values?.membro_id) : null,
				receita_bruta: currencyToNumber(values?.receita_bruta),
				faturado: values?.faturado,
				ruim: values?.ruim,
			};

			setPendingPayload(body);

			if (isEditing) updatePortfolio(body);
			else createPortfolio(body);
		},
		[createPortfolio, isEditing, updatePortfolio]
	);

	const renderRows = useCallback((): IFormRows[] => {
		return [
			{
				type: 'common',
				rowFlexProps,
				columns: [
					{
						...makeGenericFormInputStructure({
							type: 'switch',
							name: 'ruim',
							label: 'Carteira de Pedido é Ruim?',
							inputProps: {
								'data-test':
									'switch-carteira_de_pedido_e_ruim-cadastrar_editar-page-carteira_de_pedidos',
								colorScheme: 'red',
							},
						}),
					},
				],
			},
			{
				type: 'common',
				rowFlexProps,
				columns: [
					{
						...makeGenericFormInputStructure({
							type: 'text',
							name: 'codigo',
							label: 'Código da Carteira de Pedido',
							showRequiredOnLabel: true,
							validate: {
								...validateRequired(),
							},
							columnFlexProps: {
								...columnFlexProps,
								marginRight: '30px',
							},
							inputProps: {
								'data-test':
									'input-codigo_da_carteira_de_pedido-cadastrar_editar-page-carteira_de_pedidos',
								...defaultInputStyle,
								onChange: handleOnChangeCode,
							},
						}),
					},
					{
						...makeGenericFormInputStructure({
							type: 'number',
							name: 'sequencia_codigo',
							label: 'Sequência do Cód. da Carteira de Pedido',
							validate: {
								...validateMin(
									1,
									'O valor deve ser maior ou igual a 1!'
								),
								...validateByRegex(
									/^[1-9]\d*$/,
									'O valor deve ser um número inteiro válido!'
								),
							},
							inputProps: {
								'data-test':
									'input-sequencia_do_cod_da_carteira_de_pedido-cadastrar_editar-page-carteira_de_pedidos',
								...defaultInputStyle,
							},
						}),
					},
				],
			},
			{
				type: 'common',
				rowFlexProps,
				columns: [
					{
						...makeGenericFormInputStructure({
							type: 'date',
							name: 'data',
							label: 'Competência',
							showRequiredOnLabel: true,
							validate: { ...validateRequired() },
							columnFlexProps: {
								...columnFlexProps,
								marginRight: '30px',
							},
							inputProps: {
								'data-test':
									'input-competencia-cadastrar_editar-page-carteira_de_pedidos',
								...defaultInputStyle,
							},
						}),
					},
					{
						...makeGenericFormInputStructure({
							type: 'autocomplete',
							name: 'cliente_id',
							label: 'Razão Social',
							showRequiredOnLabel: false,
							inputProps: {
								'data-test':
									'autocomplete-cliente-cadastrar_editar-page-carteira_de_pedidos',
								...defaultInputStyle,
								searchType: 'cliente',
								renderInputLabel: renderClient,
								autocomplete: 'off',
								initialLabel: autocompleteAdditionalData?.client
									? renderClient(
											autocompleteAdditionalData?.client
									  )
									: '',
							},
						}),
					},
				],
			},
			{
				type: 'common',
				rowFlexProps,
				columns: [
					{
						...makeGenericFormInputStructure({
							type: 'autocomplete',
							name: 'familia_id',
							label: 'Família',
							showRequiredOnLabel: false,
							columnFlexProps: {
								...columnFlexProps,
								marginRight: '30px',
							},
							inputProps: {
								'data-test':
									'autocomplete-familia-cadastrar_editar-page-carteira_de_pedidos',
								...defaultInputStyle,
								searchType: 'familia',
								renderInputLabel: renderFamily,
								autocomplete: 'off',
								initialLabel: autocompleteAdditionalData?.family
									? renderFamily(
											autocompleteAdditionalData?.family
									  )
									: '',
								onChange: handleOnChangeFamilyWithLog,
								forceValue: forceFamilyValue,
							},
						}),
					},
					{
						...makeGenericFormInputStructure({
							type: 'autocomplete',
							name: 'produto_id',
							label: 'Produto',
							showRequiredOnLabel: true,
							validate: { ...validateRequired() },
							disabled: isDisabledProductAutocomplete,
							inputProps: {
								'data-test':
									'autocomplete-produto-cadastrar_editar-page-carteira_de_pedidos',
								...defaultInputStyle,
								searchType: 'produto',
								renderInputLabel: renderProduct,
								autocomplete: 'off',
								initialLabel:
									autocompleteAdditionalData?.product
										? renderProduct(
												autocompleteAdditionalData?.product
										  )
										: '',
								extraRequestData: {
									familia_id: familyId,
								},
								clearInput: shouldClearAutocompleteProduct,
								onChange: handleOnChangeProductWithLog,
							},
						}),
					},
				],
			},
			{
				type: 'common',
				rowFlexProps,
				columns: [
					{
						...makeGenericFormInputStructure({
							type: 'product-quantity',
							name: 'quantidade',
							label: 'Quantidade',
							showRequiredOnLabel: true,
							validate: {
								...validateRequired(),
								validate: value =>
									validateMinDecimal(
										value,
										0,
										'O valor deve ser maior ou igual a 0!'
									),
							},
							columnFlexProps: {
								...columnFlexProps,
								marginRight: '30px',
							},
							inputProps: {
								'data-test':
									'input-quantidade-cadastrar_editar-page-carteira_de_pedidos',
								...defaultInputStyle,
							},
						}),
					},
					{
						...makeGenericFormInputStructure({
							type: 'decimal',
							name: 'receita_bruta',
							label: 'Receita Bruta',
							showRequiredOnLabel: true,
							validate: { ...validateRequired() },
							inputProps: {
								'data-test':
									'input-receita_bruta-cadastrar_editar-page-carteira_de_pedidos',
								...defaultInputStyle,
								maskConfig: {
									allowNegative: false,
								},
							},
						}),
					},
				],
			},
			{
				type: 'common',
				rowFlexProps,
				columns: [
					{
						...makeGenericFormInputStructure({
							type: 'select',
							name: 'mercado_id',
							label: 'Mercado',
							inputProps: {
								'data-test':
									'select-mercado-cadastrar_editar-page-carteira_de_pedidos',
								...defaultInputStyle,
								selectOptions: marketOptions,
							},
							columnFlexProps: {
								...columnFlexProps,
								marginRight: '30px',
							},
						}),
					},
					{
						...makeGenericFormInputStructure({
							type: 'select',
							name: 'canal_venda_id',
							label: 'Canal de Venda',
							showRequiredOnLabel: false,
							inputProps: {
								'data-test':
									'select-canal_de_venda-cadastrar_editar-page-carteira_de_pedidos',
								...defaultInputStyle,
								selectOptions: salesChannelOptions,
							},
						}),
					},
				],
			},
			{
				type: 'common',
				rowFlexProps,
				columns: [
					{
						...makeGenericFormInputStructure({
							type: 'select',
							name: 'forca_venda_id',
							label: 'Força de Venda',
							inputProps: {
								'data-test':
									'select-forca_venda-cadastrar_editar-page-carteira_de_pedidos',
								...defaultInputStyle,
								selectOptions: salesForceOptions,
							},
							columnFlexProps: {
								...columnFlexProps,
								marginRight: '30px',
							},
						}),
					},
					{
						...makeGenericFormInputStructure({
							type: 'select',
							name: 'zona_venda_id',
							label: 'Zona de Venda',
							inputProps: {
								'data-test':
									'select-zona_de_venda-cadastrar_editar-page-carteira_de_pedidos',
								...defaultInputStyle,
								selectOptions: zoneOptions,
							},
						}),
					},
				],
			},
			{
				type: 'common',
				rowFlexProps,
				columns: [
					{
						...makeGenericFormInputStructure({
							type: 'select',
							name: 'membro_id',
							label: 'Membro',
							inputProps: {
								'data-test':
									'select-membro-cadastrar_editar-page-carteira_de_pedidos',
								...defaultInputStyle,
								selectOptions: memberOptions,
							},
						}),
					},
				],
			},
		];
	}, [
		salesChannelOptions,
		autocompleteAdditionalData,
		handleOnChangeFamilyWithLog,
		handleOnChangeProductWithLog,
		isDisabledProductAutocomplete,
		familyId,
		shouldClearAutocompleteProduct,
		forceFamilyValue,
		marketOptions,
		zoneOptions,
		salesForceOptions,
		memberOptions,
		handleOnChangeCode,
	]);

	const renderForm = useCallback(() => {
		if (
			isLoadingSalesChannelData ||
			IsLoadingSalesZone ||
			isLoadingCommercialHierarchy ||
			isLoadingSalesForce
		)
			return <LoadingSpinner />;

		return (
			<>
				<GenericForm
					dataTestProps={{
						cancel: 'button-cancelar-page-carteira_de_pedidos',
						save: 'button-salvar-page-carteira_de_pedidos',
					}}
					initialState={formInitialState}
					formFlexProps={formFlexProps}
					rows={renderRows()}
					style={defaultFormStyle}
					onSubmit={onSubmit}
					onCancel={goToPortfolio}
				/>
				<ModalConfirmCreateHierarchy
					isOpen={isModalOpen}
					onConfirm={handleConfirmCreateHierarchy}
					onCancel={handleCloseModal}
				/>
			</>
		);
	}, [
		formInitialState,
		renderRows,
		onSubmit,
		goToPortfolio,
		isLoadingSalesChannelData,
		IsLoadingSalesZone,
		isLoadingCommercialHierarchy,
		isLoadingSalesForce,
		isModalOpen,
		handleCloseModal,
		handleConfirmCreateHierarchy,
	]);

	return renderForm();
};

export default FormCadastrarEditarCarteira;
